/* eslint-disable react/display-name */
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, Datepicker, HookFormResolver, Text } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { Alert, ButtonGroup } from 'react-bootstrap';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { DateRequired, ObjectRequired } from '../../../../core/validations/common';
import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import ProgressBar from '../components/progress-bar';
import { useGetTaxByOperations, useGetTaxPeriodicities } from '../hooks';
import { label, labelVector, message } from '../translation';
import { type Tax, type TaxRow } from '../types';

import { type ProgressStepProps } from './types';
interface Props {
  taxes: Array<Tax>;
  onSubmit: (data: any) => void;
  data: Array<TaxRow>;
  readOnly?: boolean;
}

interface RowProps {
  idName: string;
  nameTax: string;
  expirationName: string;
  periodicityName: string;
  startName: string;
  data: TaxRow;
  errors: any;
  readOnly?: boolean;
}

const validations = yup.object().shape({
  vector: yup.array().of(
    yup.object().shape({
      periodicity: ObjectRequired,
      start: DateRequired
    })
  )
});

const RowVectorTax = ({
  data,
  idName,
  nameTax,
  expirationName,
  periodicityName,
  startName,
  readOnly,
  errors
}: RowProps) => {
  const { data: periodicities, loading, error } = useGetTaxPeriodicities(data.id);

  const err = errors ? errors['periodicity'] : undefined;

  const { getValues, setValue, register } = useFormContext();
  useEffect(() => {
    if (loading || error || !periodicities || !data.periodicity?.id) {
      return;
    }

    const selectedPeriodicity = periodicities.find((item) => item.id === data.periodicity.id);
    const currentPeriodicity = getValues(periodicityName);

    if (currentPeriodicity?.id && currentPeriodicity.id !== data.periodicity.id) {
      return;
    }

    if (!selectedPeriodicity) {
      if (currentPeriodicity) {
        setValue(periodicityName, null, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true
        });
      }
      return;
    }

    if (
      currentPeriodicity?.id !== selectedPeriodicity.id ||
      currentPeriodicity?.name !== selectedPeriodicity.name ||
      currentPeriodicity?.filingDueDay !== selectedPeriodicity.filingDueDay
    ) {
      setValue(periodicityName, selectedPeriodicity, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true
      });
    }

    if (getValues(expirationName) !== selectedPeriodicity.filingDueDay) {
      setValue(expirationName, selectedPeriodicity.filingDueDay, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false
      });
    }
  }, [
    data.periodicity?.id,
    error,
    expirationName,
    getValues,
    loading,
    periodicities,
    periodicityName,
    setValue
  ]);
  if (loading || error) {
    return <></>;
  }

  return (
    <Row>
      <Col>
        <input defaultValue={data.id} {...register(idName)} hidden={true} />
        <Text
          label=""
          name={nameTax}
          readonly={true}
          defaultValue={`${data.code} - ${data.shortName}`}
        />
      </Col>
      <Col>
        <Combobox
          defaultValue={data.periodicity}
          customError={err}
          label=""
          name={periodicityName}
          readOnly={readOnly}
          display="name"
          code="id"
          data={periodicities}
          whenChange={(item: any) => {
            if (item) {
              setValue(expirationName, item.filingDueDay);
            }
          }}
        />
      </Col>
      <Col>
        <Text defaultValue={data.expiration} label="" name={expirationName} readonly={true} />
      </Col>

      <Col>
        <Datepicker customError={err} label="" name={startName} />
      </Col>
    </Row>
  );
};

export const TaxVector = ({ onSubmit, taxes: taxesByOperations, data, readOnly }: Props) => {
  const { t, i18n } = useTranslation();
  const [taxes, setTaxes] = useState<Array<TaxRow>>([]);
  const [init, setInit] = useState<boolean>(false);

  const submit = (result: any) => {
    onSubmit(result);
  };

  useEffect(() => {
    const newData = [] as Array<TaxRow>;

    taxesByOperations.forEach((x) => {
      const newValue = data?.find((y) => y.id === x.id);
      if (newValue) {
        newData.push({ ...newValue, shortName: x.shortName, code: x.code });
      } else {
        newData.push({ id: x.id, shortName: x.shortName, code: x.code } as TaxRow);
      }
    });
    setTaxes(newData ?? []);
    setInit(true);
  }, []);

  if (!init) {
    return <></>;
  }

  return (
    <>
      <HookFormResolver
        initialValues={{ vector: taxes }}
        nameForm="formTaxVector"
        onSubmit={submit}
        validations={validations}
      >
        {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
            control,
            name: 'vector',
            keyName: 'fieldId'
          });

          const hasData = fields?.length > 0;
          return (
            <>
              {hasData && (
                <>
                  <hr />
                  <Row>
                    <Col>{label(t, 'taxObligation')}</Col>
                    <Col>{labelVector(t, 'periodicity')}</Col>
                    <Col>{labelVector(t, 'expiration')}</Col>
                    <Col>{labelVector(t, 'startOperation')}</Col>
                  </Row>
                </>
              )}
              {fields.map((obj: any, index: number) => {
                const err = errors?.vector ? (errors?.vector as any)[index] : undefined;

                return (
                  <RowVectorTax
                    readOnly={readOnly}
                    errors={err}
                    data={obj}
                    key={obj.fieldId}
                    idName={`vector[${index}].id`}
                    nameTax={`vector[${index}].tax`}
                    expirationName={`vector[${index}].expiration`}
                    periodicityName={`vector[${index}].periodicity`}
                    startName={`vector[${index}].start`}
                  />
                );
              })}
              {!hasData && (
                <>
                  <hr />
                  <Row>
                    <Col>
                      <Alert variant="warning">{message(t, 'withoutOperationsVector')}</Alert>
                    </Col>
                  </Row>
                </>
              )}
            </>
          );
        }}
      </HookFormResolver>
    </>
  );
};

export default (props: ProgressStepProps) => {
  const operations = useTaxpayerRequestStore((s) => s.form.operations);
  const operationsIds = useMemo(() => operations?.map((x) => x.id) as Array<string>, [operations]);
  const taxSelected = useTaxpayerRequestStore((s) => s.form.taxes);
  const addTaxPeriodicities = useTaxpayerRequestStore((s) => s.addTaxPeriodicities);
  const [{ data, loading, error }] = useGetTaxByOperations(
    operationsIds,
    operationsIds?.length > 0
  );

  const onSubmit = (data: any) => {
    if (data?.vector) {
      addTaxPeriodicities(data.vector);
    }
    props.nextStep();
  };
  if (loading || error) {
    return <></>;
  }
  return (
    <>
      <TaxVector taxes={data || []} onSubmit={onSubmit} data={taxSelected ? taxSelected : []} />
      <ProgressBar percentage={props.percentage} />
      <BtnToolbar>
        <ButtonGroup>
          <Button
            form="formTextData"
            action={ButtonAction.previous}
            onClick={() => props.previousStep()}
          />
        </ButtonGroup>
        <ButtonGroup>
          <Button variant="primary" form="formTaxVector" action={ButtonAction.next} type="submit" />
        </ButtonGroup>
      </BtnToolbar>
    </>
  );
};
